'use client';

import { useCallback } from 'react';
import Image from 'next/image';
import { useTranslations } from 'next-intl';
import clsx from 'clsx';
import { GlowPrice, GlowSelector, PropsWithFontFamily, PropsWithLocale } from '@headless-workspace/glow-ds';
import { StringHelpers } from '@headless-workspace/utils';
import { ChipStatus, ProductType } from '../../../../../client';
import { ProductDetailVariantValue } from '../../../../../server';
import { getVariantImageClassNames } from '../../../style';

type VariantSelectorListProps = PropsWithFontFamily &
    PropsWithLocale & {
        type: ProductType;
        variants: ProductDetailVariantValue[];
        squaredShade: boolean;
        getStockInformation: (isAvailable: boolean) => {
            label: string;
            status: ChipStatus;
        };
        priceAlwaysBlack?: boolean;
        enableDiscountInfo: boolean;
        currency: string;
        onVariantSelected: (variantId: string) => void;
        selectedVariantId?: string;
        isPriceLegalMentionEnabled: boolean;
    };

export const VariantSelectorList = ({
    type,
    variants,
    squaredShade,
    priceAlwaysBlack,
    enableDiscountInfo,
    currency,
    locale,
    fontFamily,
    getStockInformation,
    onVariantSelected,
    selectedVariantId,
    isPriceLegalMentionEnabled,
}: VariantSelectorListProps) => {
    const t = useTranslations('Discover.Pdp.information.selector');

    const handleVariantSelected = useCallback(
        (variantId: string) => () => {
            onVariantSelected(variantId);
        },
        [onVariantSelected],
    );

    if (!type) {
        return null;
    }

    const { imageClassName, shapeClassName } = getVariantImageClassNames(type, squaredShade);

    return (
        <ul className={'flex flex-col gap-0.5 pb-0.125'}>
            {variants.map((variant) => (
                <li
                    key={variant.id}
                    className={clsx(
                        'flex flex-row justify-between',
                        variant.id === selectedVariantId && 'outline outline-bold outline-border-brand rounded-0.5',
                    )}
                >
                    <GlowSelector
                        image={variant.thumbnailImage}
                        ImageTagName={Image}
                        imageClassName={imageClassName}
                        shapeClassName={shapeClassName}
                        titleContent={{
                            label: variant.name,
                            fontWeight: 'medium',
                        }}
                        subTitleContent={
                            variant.pricePerUnit
                                ? {
                                      label: variant.pricePerUnit,
                                      color: 'tertiary',
                                  }
                                : undefined
                        }
                        chipWithText={getStockInformation(variant.isAvailable)}
                        RightElement={
                            <GlowPrice
                                price={StringHelpers.formatPriceWithConditions(
                                    variant.price,
                                    currency,
                                    locale,
                                    isPriceLegalMentionEnabled,
                                )}
                                priceBeforeDiscount={StringHelpers.formatPrice({
                                    price: variant.priceBeforeDiscount,
                                    currency,
                                    locale,
                                    defaultValue: null,
                                })}
                                discountPercentageLabel={t('modal.label.discountPercentage', {
                                    discountPercentage: variant.discountPercentage,
                                })}
                                priceAlwaysBlack={priceAlwaysBlack}
                                enableDiscountInfo={enableDiscountInfo}
                                fontFamily={fontFamily}
                                className={'items-end'}
                            />
                        }
                        fontFamily={fontFamily}
                        onClick={handleVariantSelected(variant.id)}
                        isAvailable={variant.isAvailable}
                    />
                </li>
            ))}
        </ul>
    );
};
